import { executeAnalyticsQuery } from "../api";
import { readNumber } from "../api/analytics-rows";
import { GENERATIVE_REQUEST_TIMEOUT_MS } from "../api/timeouts";
import type { SummaryLength } from "../api/analytics";
import type { Response } from "../types";
import { buildTopicSentimentBandQuery, type TopicSentimentBand } from "./sentiment-slices";

export type TopicSentimentBandAnalysis = {
  sentimentBand: TopicSentimentBand;
  responseCount: number;
  aggregateSentiment: number | null;
  hasSentimentData: boolean;
  summary?: string;
  summaryError?: string;
  summaryResponses: Response[];
};

export type FetchTopicSentimentBandAnalysisOptions = {
  topicRecordId: string;
  rollupRecordIds: string[];
  dateWindow?: { from?: string; to?: string };
  dataFileIds?: string[];
  sentimentAggregationFn: string;
  sampleLimit?: number;
  summaryLength?: SummaryLength;
};

export async function fetchTopicSentimentBandAnalyses(
  options: FetchTopicSentimentBandAnalysisOptions,
): Promise<TopicSentimentBandAnalysis[]> {
  const [negative, positive] = await Promise.all([
    fetchTopicSentimentBandAnalysis({ ...options, sentimentBand: "negative" }),
    fetchTopicSentimentBandAnalysis({ ...options, sentimentBand: "positive" }),
  ]);
  return [negative, positive];
}

async function fetchTopicSentimentBandAnalysis(
  options: FetchTopicSentimentBandAnalysisOptions & { sentimentBand: TopicSentimentBand },
): Promise<TopicSentimentBandAnalysis> {
  const result = await executeAnalyticsQuery(
    {
      query: buildTopicSentimentBandQuery({
        topicRecordId: options.topicRecordId,
        rollupRecordIds: options.rollupRecordIds,
        dateWindow: options.dateWindow,
        dataFileIds: options.dataFileIds,
        sentimentAggregationFn: options.sentimentAggregationFn,
        sentimentBand: options.sentimentBand,
        includeSummary: true,
        limit: 1,
      }),
      options: {
        sample_limit: options.sampleLimit ?? 20,
        summary_length: options.summaryLength ?? "standard",
      },
    },
    undefined,
    { timeoutMs: GENERATIVE_REQUEST_TIMEOUT_MS },
  );

  const row = result.data[0];
  const metrics = row?.metrics ?? {};
  const responseCount = readNumber(metrics, ["response_count"]) ?? 0;
  const aggregateSentiment = readNumber(metrics, ["aggregate_sentiment", "metric_aggregate_sentiment"]);

  return {
    sentimentBand: options.sentimentBand,
    responseCount,
    aggregateSentiment,
    hasSentimentData: aggregateSentiment !== null,
    ...(result.summary !== undefined ? { summary: result.summary } : {}),
    ...(result.summary_error !== undefined ? { summaryError: result.summary_error } : {}),
    summaryResponses: result.summaryResponses ?? [],
  };
}
